import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useState } from 'react';

import { reviewerHasuraContext } from '@eluve/apollo-client';
import {
  HStack,
  NewButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@eluve/components';
import { DbEnums } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';

import { useAppointmentParams } from '../useAppointmentParams';

import { useGoldenSummaryContext } from './GoldenSummaryContextProvider';
import {
  createGoldenSummaryMutation,
  finalizeGoldenSummaryMutation,
} from './operations';

interface GoldenSummarySelectProps {
  onSelectSummary: (summaryId: string) => void;
  originalContent?: Record<string, unknown>;
}

const listGoldenSummaries = graphql(`
  query getGoldenNoteSummary($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
      __typename
      id
      mode
      appointment_golden_notes(orderBy: { createdAt: DESC }) {
        __typename
        id
        content
        status
        createdAt
        updatedAt
      }
    }
  }
`);

export const GoldenSummarySelect = ({
  onSelectSummary,
  originalContent,
}: GoldenSummarySelectProps) => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const { hasPendingChanges } = useGoldenSummaryContext();
  const toast = useToast();

  const { data, refetch } = useSuspenseQuery(listGoldenSummaries, {
    context: reviewerHasuraContext,
    variables: { tenantId, appointmentId },
  });
  const goldenNotes = data?.appointmentsByPk?.appointment_golden_notes ?? [];
  const activeNote = goldenNotes.find((note) => note.id === activeNoteId);

  const [createGoldenSummary] = useMutation(createGoldenSummaryMutation);
  const [finalizeGoldenSummary] = useMutation(finalizeGoldenSummaryMutation);

  const handleCreateGoldenSummary = async () => {
    const result = await createGoldenSummary({
      variables: {
        appointmentId,
        tenantId,
        outputType: DbEnums.LlmOutputType.SOAP_NOTE,
        content: originalContent ?? {},
      },
    });
    if (result.data?.insertAppointmentGoldenNotesOne?.id) {
      toast.success('Golden summary created');
      await refetch();
      setActiveNoteId(result.data.insertAppointmentGoldenNotesOne.id);
      onSelectSummary(result.data.insertAppointmentGoldenNotesOne.id);
    }
  };

  const handleFinalizeGoldenSummary = async () => {
    if (!activeNoteId) return;
    await finalizeGoldenSummary({
      variables: {
        id: activeNoteId,
        tenantId,
      },
    });
    toast.success('Golden summary finalized');
  };
  return (
    <HStack gap={4} className="py-1.5" justify="between">
      <Select
        value={activeNoteId ?? ''}
        disabled={hasPendingChanges || goldenNotes.length === 0}
        onValueChange={(noteId: string) => {
          setActiveNoteId(noteId);
          onSelectSummary(noteId);
        }}
      >
        <SelectTrigger className="grow rounded-lg p-3">
          <SelectValue placeholder="Select a version" />
        </SelectTrigger>
        <SelectContent className="rounded-lg">
          {goldenNotes.map((note) => (
            <SelectItem key={note.id} value={note.id}>
              {new Date(note.createdAt).toLocaleString()} ({note.status})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <HStack wFull={false}>
        {activeNote && activeNote.status !== 'COMPLETED' && (
          <NewButton
            text="Finalize"
            type="primary"
            icon={{ name: 'CheckSquare' }}
            onClick={handleFinalizeGoldenSummary}
            loading={hasPendingChanges}
          />
        )}
        <NewButton
          text="New Golden Summary"
          type="primary"
          icon={{ name: 'PlusCircle' }}
          onClick={handleCreateGoldenSummary}
          loading={hasPendingChanges}
        />
      </HStack>
    </HStack>
  );
};
